/**
 * The one vocabulary for an AI Service's access policy (task 973).
 *
 * Report: `project/reports/innovate-app-service-access-policy-management.md`.
 *
 * Four surfaces name the same thing — the Config tab's Access section, the
 * detail page header badge, the services table indicator, and the Interface
 * tab's publishing card — so the labels and the plain sentences live here
 * rather than being spelled three slightly different ways.
 *
 * `custom` is deliberately absent from `EDITABLE_ACCESS_MODES`: innovate-service
 * answers every call to a `custom`-mode service with `501 AUTH_MODE_UNAVAILABLE`
 * (see `project/docs/developer/innovate-endpoint-auth-policy.md`), so it is not
 * on the menu. It still has a label and a sentence, because a legacy row can
 * carry it and the page has to be able to say what it is.
 */
import type { AuthPolicyMode, Endpoint } from '@/types';

/** Badge-sized label — the header, the table, the publishing card. */
export const ACCESS_MODE_LABELS: Record<AuthPolicyMode, string> = {
  public: 'Public',
  apiKey: 'API key',
  jwt: 'Sign-in required',
  custom: 'Custom',
};

/** A full sentence for the Access section and the table's tooltip. */
export const ACCESS_MODE_SENTENCES: Record<AuthPolicyMode, string> = {
  public: 'Anyone can call this service.',
  apiKey: 'Callers need an account API key.',
  jwt: 'Only signed-in users of this account can call this service.',
  custom: 'This service uses a custom validator, which is not available yet — calls are refused.',
};

/** The modes the selector offers, in the order it offers them. */
export const EDITABLE_ACCESS_MODES: readonly AuthPolicyMode[] = ['public', 'apiKey', 'jwt'];

/**
 * The mode a service is actually on.
 *
 * Mirrors the service's own `parseAuthPolicy`, which fails CLOSED: a missing or
 * malformed policy is enforced as `jwt`, so the app must not report it as
 * anything friendlier.
 */
export function serviceAccessMode(endpoint: Pick<Endpoint, 'authPolicy'>): AuthPolicyMode {
  return endpoint.authPolicy?.mode ?? 'jwt';
}

/**
 * The API-key block's sentence, given how many keys the account has (task 980).
 *
 * `null` means the app does not know — the list has not answered yet, or it was
 * refused (a backend feature gate answers 403). The countless sentence is then
 * the honest fallback: the rule it states is true regardless of the count, and
 * a wrong number would be worse than no number.
 *
 * Any active key of the account satisfies any API-key-mode service in it; there
 * is no per-service scoping, which is why the sentence counts the ACCOUNT's
 * keys rather than the service's.
 */
export function apiKeyAccessSentence(activeKeyCount: number | null): string {
  if (activeKeyCount === null) return 'Any active API key for this account can call this service.';
  if (activeKeyCount === 0) return 'No API keys yet; create one on the API Keys page.';
  return activeKeyCount === 1
    ? '1 active API key can call this service.'
    : `${activeKeyCount} active API keys can call this service.`;
}
